import React from 'react';
import { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { TVaultSelectProps } from '@/console/features/vaults/components/vault-select/vault-select.types';
import { TSelectOption } from '@/shared/components/ui/form/components/searchable-select/base-searchable-select.types';
import { AsyncSearchableSelect } from '@/shared/components/ui/form/components/searchable-select/variant/async/async-searchable-select';
import { useReadAllVaultsQuery } from '@/shared/redux/api/assets/vaults/vaults-api';
import {
  ReadAllVaultsQuery,
  VaultFragment,
} from '@/shared/types/types.generated';

export const VaultSelect = <
  TFieldValues extends FieldValues,
  TOptionShape = string,
>({
  name,
  createOption,
  disableTags = true,
  enableTagsTooltip,
  filterSelectedTags,
  additionalOptions = [],
  onSelectChange,
  getItemId,
  required,
  vaultFilter,
  multiple = false,
}: TVaultSelectProps<TFieldValues, TOptionShape>) => {
  const { t } = useTranslation();
  const createDefaultOption = (vault: VaultFragment) => {
    return {
      label: vault.name,
      value: vault.vaultId,
    } as TSelectOption<TOptionShape>;
  };
  const _createOption = createOption ? createOption : createDefaultOption;

  const transformData = (data?: ReadAllVaultsQuery) => {
    if (!data) {
      return [];
    }

    const vaults = data.readAllVaults.items;
    const validVaults = vaultFilter
      ? vaults.filter((vault) => vaultFilter(vault))
      : vaults;

    const options = validVaults.map((vault) => _createOption(vault));

    return [...additionalOptions, ...options];
  };

  return (
    <AsyncSearchableSelect<TFieldValues, ReadAllVaultsQuery, TOptionShape>
      name={name}
      label={t('vault:select_vault')}
      placeholder={t('vault:search_vault')}
      disableTags={disableTags}
      enableTagsTooltip={enableTagsTooltip}
      filterSelectedTags={filterSelectedTags}
      getItemId={getItemId}
      optionProps={{
        useRTKQuery: useReadAllVaultsQuery,
        transformData,
      }}
      onSelectChange={onSelectChange}
      required={required}
      multiple={multiple}
    />
  );
};
